/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { Pressable, Text, View, Icon } from '@components/atoms'
import { Drawer as MuiDrawer } from '@mui/material'
import Badge from '@mui/material/Badge'

import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector, configActions } from '@redux/index'
import { HomeNavMenuType } from '@redux/types'
import { grey } from '@mui/material/colors'
import config from '@configs'

interface PropsType {
	menuList: HomeNavMenuType[]
}

export const HomeDrawer = (props: PropsType) => {
	const { menuList } = props

	// hook
	const navigate = useNavigate()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const isHomeOpenDrawer: boolean = useAppSelector(state => state.config.isHomeOpenDrawer)
	const isLogedin: boolean = useAppSelector(state => state.customerAuth.isLogedin)
	const customerRoles: string[] = useAppSelector(
		state => state.customerAuth.customer.roles,
	)

	const customerMenuList: HomeNavMenuType[] = config.customerMenu(customerRoles)
	// 2026-09-09 사이트맵 §9: 데스크톱은 드롭다운이 없고, 하위 메뉴는 이 드로어에서만 펼친다.
	const subMenuObj: { [id: string]: HomeNavMenuType[] } = {
		products: config.productMenu(),
		support: config.supportMenu(),
	}

	// state
	const [cartLength, setCartLength] = React.useState<number>(0)

	const onToggleClick = React.useCallback(() => {
		reduxDispatch(configActions.IS_HOME_OPEN_DRAWER(!isHomeOpenDrawer))
	}, [isHomeOpenDrawer])

	const onMenuPress = (aMenu: HomeNavMenuType) => {
		onToggleClick()
		navigate(aMenu.uri)
		// 같은 페이지 안의 앵커(/products#kedem)는 라우트가 안 바뀌어 스크롤이 안 일어난다 — 직접 내린다.
		const hashIdx = aMenu.uri.indexOf('#')
		if (hashIdx !== -1) {
			const id = aMenu.uri.slice(hashIdx + 1)
			window.setTimeout(() => {
				document.getElementById(id)?.scrollIntoView({ block: 'start' })
			}, 350)
		}
	}

	return (
		<MuiDrawer
			css={style}
			open={isHomeOpenDrawer}
			anchor="right"
			onClose={onToggleClick}
			variant="temporary"
		>
			<View className="homeDrawerOuterView">
				<View className="drawerTopView">
					{isLogedin ? (
						<Pressable
							className="drawerTopButton"
							onClick={() => navigate('/customer/info')}
						>
							<Icon name="account-outline" css={{ fontSize: '26px' }} />
						</Pressable>
					) : (
						<Pressable
							className="drawerTopButton"
							onClick={() => navigate('/customer/login')}
						>
							<Text>Login</Text>
						</Pressable>
					)}
				</View>

				<View className="mainMenu">
					<View className="drawerListView">
						{menuList.map(aMenu => {
							const subList = subMenuObj[aMenu.id] ?? []
							return (
								<React.Fragment key={aMenu.id}>
									<Pressable className="navMenuButton" onClick={() => onMenuPress(aMenu)}>
										<Text>{aMenu.title}</Text>
									</Pressable>
									{subList.map(aSub => (
										<Pressable
											key={aSub.id}
											className="navMenuButton navSubMenuButton"
											onClick={() => onMenuPress(aSub)}
										>
											<Text>{aSub.title}</Text>
										</Pressable>
									))}
								</React.Fragment>
							)
						})}
					</View>
				</View>

				{isLogedin && (
					<View className="customerMenu">
						<View className="drawerListView">
							{customerMenuList.map(aMenu => {
								return (
									<Pressable
										key={aMenu.id}
										className="navMenuButton"
										onClick={() => onMenuPress(aMenu)}
									>
										<Text>{aMenu.title}</Text>
									</Pressable>
								)
							})}
						</View>
					</View>
				)}
			</View>
		</MuiDrawer>
	)
}

const style = css({
	'.homeDrawerOuterView': {
		maxHeight: '40rem',
	},
	'.drawerTopView': {
		width: '100%',
		marginTop: '2rem',
		paddingBottom: '2rem',
		borderBottom: '1px solid',
		borderColor: grey['200'],

		'.drawerTopButton': {
			width: '100%',
			height: '3rem',
		},
	},

	'.drawerListView': {
		width: '20rem',

		'.navMenuButton': {
			width: '100%',
		},
		// 드로어 항목은 가운데 정렬이라 들여쓰기 대신 크기·색으로 층을 만든다.
		'.navSubMenuButton': {
			minHeight: '2.2rem',
			p: {
				fontSize: '0.9rem',
				color: grey[600],
			},
		},
	},

	'.mainMenu': {
		marginTop: '5rem',
	},

	'.customerMenu': {
		position: 'absolute',
		bottom: 0,
		paddingBottom: '3rem',
		backgroundColor: grey[900],

		p: {
			color: 'white',
		},
	},
})
